import React, { createContext, useState, useContext, useLayoutEffect } from 'react';

const lightBase = {
    '--text-color': '#1a1a2e',
    '--text-secondary': '#64748b',
    '--bg-header': '#ffffff',
    '--bg-footer': '#ffffff',
    '--bg-content': '#ffffff',
    '--bg-modal-header': '#f8fafc',
    '--brand': "url('/assets/logos/nVisio.png')",
    '--blackbee': "url('/assets/logos/BB_Black.png')",
    '--border-color': '#e2e8f0',
    '--border-light': '#f1f5f9',
    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.04)',
    '--shadow-md': '0 4px 12px rgba(0, 0, 0, 0.06)',
    '--shadow-lg': '0 12px 40px rgba(0, 0, 0, 0.1)',
    '--shadow-header': '0 1px 3px rgba(0, 0, 0, 0.06)',
    '--card-bg': '#ffffff',
    '--input-bg': '#f8fafc',
    '--input-border': '#e2e8f0',
    '--scrollbar-track': '#f1f5f9',
    '--scrollbar-thumb': '#cbd5e1',
    '--overlay-bg': 'rgba(15, 23, 42, 0.4)',
};

const themes = {
    'ocean-teal': {
        ...lightBase,
        '--bg-body': '#f0f4f8',
        '--bg-menu': '#f8fafb',
        '--accent': '#0d9488',
        '--accent-hover': '#0f766e',
        '--accent-light': 'rgba(13, 148, 136, 0.08)',
        '--link-color': '#0d9488',
        '--input-focus': '#0d9488',
        '--menu-hover': '#f0fdfa',
        '--menu-active': '#ccfbf1',
        '--badge-bg': '#ccfbf1',
        '--badge-text': '#0d9488',
        '--chart-bg': '#f0f4f8',
        '--chart-text': '#004d40',
        '--chart-1': '#0d9488',
        '--chart-2': '#14b8a6',
        '--chart-3': '#2dd4bf',
        '--chart-4': '#5eea8d',
        '--chart-5': '#99f6e4',
        '--chart-6': '#ccfbf1',
    },
    'slate-blue': {
        ...lightBase,
        '--bg-body': '#f1f5f9',
        '--bg-menu': '#fafbfc',
        '--accent': '#3b82f6',
        '--accent-hover': '#2563eb',
        '--accent-light': 'rgba(59, 130, 246, 0.08)',
        '--link-color': '#3b82f6',
        '--input-focus': '#3b82f6',
        '--menu-hover': '#eff6ff',
        '--menu-active': '#dbeafe',
        '--badge-bg': '#dbeafe',
        '--badge-text': '#2563eb',
        '--chart-bg': '#f1f5f9',
        '--chart-text': '#1e3a8a',
        '--chart-1': '#3b82f6',
        '--chart-2': '#2563eb',
        '--chart-3': '#1d4ed8',
        '--chart-4': '#1e40af',
        '--chart-5': '#1e3a8a',
        '--chart-6': '#1e3a8a',
    },
    'warm-amber': {
        ...lightBase,
        '--bg-body': '#faf9f7',
        '--bg-menu': '#fdfcfa',
        '--accent': '#d97706',
        '--accent-hover': '#b45309',
        '--accent-light': 'rgba(217, 119, 6, 0.08)',
        '--link-color': '#d97706',
        '--input-focus': '#d97706',
        '--menu-hover': '#fffbeb',
        '--menu-active': '#fef3c7',
        '--badge-bg': '#fef3c7',
        '--badge-text': '#b45309',
        '--chart-bg': '#faf9f7',
        '--chart-text': '#78350f',
        '--chart-1': '#d97706',
        '--chart-2': '#f59e0b',
        '--chart-3': '#fbbf24',
        '--chart-4': '#fcd34d',
        '--chart-5': '#fde68a',
        '--chart-6': '#fef3c7',
    },
    dark: {
        '--bg-body': '#0f1117',
        '--bg-header': '#1a1b23',
        '--bg-menu': '#1a1b23',
        '--bg-footer': '#1a1b23',
        '--text-color': '#e2e8f0',
        '--text-secondary': '#94a3b8',
        '--bg-content': '#1e1f2a',
        '--bg-modal-header': '#252633',
        '--link-color': '#818cf8',
        '--brand': "url('/assets/logos/nVisio_inv.png')",
        '--blackbee': "url('/assets/logos/BB_White.png')",
        '--accent': '#818cf8',
        '--accent-hover': '#6366f1',
        '--accent-light': 'rgba(129, 140, 248, 0.1)',
        '--border-color': '#2d2e3a',
        '--border-light': '#252633',
        '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.2)',
        '--shadow-md': '0 4px 12px rgba(0, 0, 0, 0.3)',
        '--shadow-lg': '0 12px 40px rgba(0, 0, 0, 0.5)',
        '--shadow-header': '0 1px 3px rgba(0, 0, 0, 0.3)',
        '--menu-hover': '#252633',
        '--menu-active': 'rgba(129, 140, 248, 0.12)',
        '--card-bg': '#1e1f2a',
        '--input-bg': '#252633',
        '--input-border': '#2d2e3a',
        '--input-focus': '#818cf8',
        '--badge-bg': 'rgba(129, 140, 248, 0.15)',
        '--badge-text': '#818cf8',
        '--scrollbar-track': '#1a1b23',
        '--scrollbar-thumb': '#2d2e3a',
        '--overlay-bg': 'rgba(0, 0, 0, 0.6)',
        '--chart-bg': '#1e1f2a',
        '--chart-text': '#e2e8f0',
        '--chart-1': '#818cf8',
        '--chart-2': '#6366f1',
        '--chart-3': '#4f46e5',
        '--chart-4': '#4338ca',
        '--chart-5': '#3730a3',
        '--chart-6': '#312e81',
    }
};

const themeLabels = {
    'ocean-teal': '🌊 Ocean Teal',
    'slate-blue': '💎 Slate Blue',
    'warm-amber': '🔥 Warm Amber',
    'dark': '🌙 Dark',
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    const [themeName, setThemeName] = useState(() => {
        return localStorage.getItem('theme') || 'ocean-teal';
    });

    useLayoutEffect(() => {
        const theme = themes[themeName];
        if (!theme) return;
        for (const key in theme) {
            document.documentElement.style.setProperty(key, theme[key]);
        }
        localStorage.setItem('theme', themeName);
    }, [themeName]);

    const changeTheme = (name) => {
        if (themes[name]) setThemeName(name);
    };

    return (
        <ThemeContext.Provider value={{ themeName, changeTheme, themeLabels, themes }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
